import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { AiFillEye, AiFillEyeInvisible, AiOutlineDelete } from "react-icons/ai";
import { ReactComponent as ArrowBackIcon } from "assets/images/common/svgs/arrow_back.svg";
import { useNavigate } from "react-router-dom";
import editProfileUSer from "assets/images/common/others/editProfileUSer.png";
import useTeamMembers from "containers/team-management/useHooks";
import { teamMemberDTO } from "utils/helpers/models/team-management/team-members.dto";
import { TEAM_MEMBER_ROLES } from "utils/helpers/enums/roles.enum";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { EditProfile } from "assets/images/common/svgs";
import { warningToaster } from "utils/helpers/common/alert-service";

const CreateTeamMember: React.FC = () => {
  const {
    register,
    handleSubmit,
    clearErrors,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<teamMemberDTO>();
  const [formData, setFormData] = useState<teamMemberDTO>(new teamMemberDTO());
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { addCreateTeamMember, uploadFile, getRolesInTeam } = useTeamMembers();
  const [roles, setRoles] = useState<any[]>([]);

  useEffect(() => {
    getRolesInTeam(setRoles, { page: 0, size: 100 });
  }, []);


  const uploadProfilePic = (event: any) => {
    const { files } = event.target;
    const file = files[0];
    setValue("profilePicUrl", file);
    setFormData({ ...formData, profilePicUrl: URL.createObjectURL(file) });
  };

  const handleProfilePicDelete = () => {
    setValue("profilePicUrl", "");
    setFormData({ ...formData, profilePicUrl: "" });
  };

  const onSubmit = async (data: teamMemberDTO) => {
    const profilePic: any = getValues("profilePicUrl");
    if (!profilePic) {
      warningToaster("Please upload your profile picture");
      return;
    }
    if (typeof profilePic !== "string" && profilePic instanceof Blob) {
      const form_data = new FormData();
      form_data.append("file", profilePic);
      const imageData: any = await uploadFile(form_data);
      formData.profilePicId = imageData?.id;
    }

    const selectedRole = roles.find(
      (role) => role.id === Number(data.roleId)
    );

    if (!selectedRole) {
      warningToaster("Selected role not found");
      return;
    }

    const payload = {
      ...data,
      roleId: selectedRole.id,
      defaultRole: selectedRole.name,
      profilePicId: formData.profilePicId,
    };

    await addCreateTeamMember(payload);
  };

  const goToBack = () => {
    navigate(-1);
  };

  return (
    <div className="">
      <h1 className="text-base text-primaryText font-bold mb-6 flex items-center gap-3">
        <span
          onClick={goToBack}
          className="bg-transparentBlueBg w-[44px] h-[44px] rounded-full flex items-center justify-center cursor-pointer"
        >
          <ArrowBackIcon />
        </span>
        Add new Team Member
      </h1>

      <div className="w-full bg-white p-4 rounded-[8px]">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full flex flex-col gap-3 mt-3"
        >
          <div>
            <div className="flex flex-col items-center w-[270px]">
              <div className="relative">
                <img
                  src={
                    formData.profilePicUrl
                      ? concatPathWithBackendUrl(formData.profilePicUrl)
                      : editProfileUSer
                  }
                  alt="Profile"
                  className="w-36 h-36 rounded-full object-cover"
                />
                {formData.profilePicUrl ? (
                  <button
                    onClick={handleProfilePicDelete}
                    type="button"
                    className="absolute bottom-2 right-0 bg-red-500 text-white p-2 rounded-full cursor-pointer"
                  >
                    <AiOutlineDelete />
                  </button>
                ) : (
                  <label
                    htmlFor="profilePicUrl"
                    className="absolute bottom-2 right-0 bg-white shadow p-2 rounded-full cursor-pointer"
                  >
                    <EditProfile />
                  </label>
                )}
                <input
                  id="profilePicUrl"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  {...register("profilePicUrl", { required: false })}
                  onChange={uploadProfilePic}
                />
              </div>
              {errors.profilePicUrl && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.profilePicUrl.message}
                </p>
              )}
              <div className="flex flex-col gap-2 items-center mt-3">
                <p className="text-base text-label px-5 font-semibold">
                  Upload Profile Picture
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {/* First Name */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">First Name</label>
              <input
                type="text"
                placeholder="Enter First Name"
                {...register("firstName", {
                  required: "First Name is required",
                })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.firstName && (
                <span className="text-red-500 text-sm">
                  {errors.firstName.message}
                </span>
              )}
            </div>

            {/* Last Name */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Last Name</label>
              <input
                type="text"
                placeholder="Enter Last Name"
                {...register("lastName", { required: "Last Name is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.lastName && (
                <span className="text-red-500 text-sm">
                  {errors.lastName.message}
                </span>
              )}
            </div>

            {/* Email */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Email</label>
              <input
                type="email"
                placeholder="Enter Email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Invalid email address",
                  },
                })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.email && (
                <span className="text-red-500 text-sm">
                  {errors.email.message}
                </span>
              )}
            </div>

            {/* Gender */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Gender</label>
              <select
                {...register("gender", { required: "Gender is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none bg-white"
              >
                <option value="">Select Gender</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
              {errors.gender && (
                <span className="text-red-500 text-sm">
                  {errors.gender.message}
                </span>
              )}
            </div>

            {/* Date of Birth */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Date of Birth</label>
              <input
                type="date"
                {...register("dob", { required: "Date of Birth is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.dob && (
                <span className="text-red-500 text-sm">
                  {errors.dob.message}
                </span>
              )}
            </div>

            {/* Language */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Language</label>
              <input
                type="text"
                placeholder="Enter Language"
                {...register("language", { required: "Language is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.language && (
                <span className="text-red-500 text-sm">
                  {errors.language.message}
                </span>
              )}
            </div>

            {/* State */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">State</label>
              <input
                type="text"
                placeholder="Enter State"
                {...register("state", { required: "State is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.state && (
                <span className="text-red-500 text-sm">
                  {errors.state.message}
                </span>
              )}
            </div>

            {/* City */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">City</label>
              <input
                type="text"
                placeholder="Enter City"
                {...register("city", { required: "City is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.city && (
                <span className="text-red-500 text-sm">
                  {errors.city.message}
                </span>
              )}
            </div>

            {/* Zip Code */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Zip Code</label>
              <input
                type="text"
                placeholder="Enter Zip Code"
                {...register("zipCode", { required: "Zip Code is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.zipCode && (
                <span className="text-red-500 text-sm">
                  {errors.zipCode.message}
                </span>
              )}
            </div>

            {/* Password */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter Password"
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters",
                    },
                  })}
                  className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
                />
                <div
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <AiFillEyeInvisible size={20} />
                  ) : (
                    <AiFillEye size={20} />
                  )}
                </div>
              </div>
              {errors.password && (
                <span className="text-red-500 text-sm">
                  {errors.password.message}
                </span>
              )}
            </div>

            {/* Status */}
            {/* <div className="flex flex-col gap-2">
              <label className="text-labelText">Status</label>
              <select
                {...register("status", { required: "Status is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none bg-white"
              >
                <option value="">Select Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              {errors.status && (
                <span className="text-red-500 text-sm">
                  {errors.status.message}
                </span>
              )}
            </div> */}

            <div className="flex flex-col gap-2">
              <label className="text-labelText">Role</label>
              <select
                {...register("roleId", { required: "Role is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none bg-white"
              >
                <option value="">Select Role</option>
                {roles.map((type) => (
                  <option value={type.id.toString()} key={type.id} className="capitalize">
                    {type.name}
                  </option>
                ))}
              </select>


              {errors.roleId && (
                <span className="text-red-500 text-sm">
                  {errors.roleId.message}
                </span>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-start mt-5">
            <button
              type="submit"
              className="w-[125px] h-[45px] text-xl font-semibold text-white bg-darkTealBlue rounded-[8px]"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTeamMember;
